import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { GeoDiscount } from 'shared/types/cart/Discount';
import { ProductDetails } from 'shared/types/product/Product';
import { Form } from 'components/ui/atoms';
import { hasBundleId, hasProfessionalCertificate } from 'utils/bundle-id';
import { getDiscountByCountry } from 'utils/discount/getDiscountByCountry';
import { elementEvents, ElementType } from 'utils/eventing';
import { MonarchDataConsumer } from 'utils/eventing/monarch/MonarchDataConsumer';
import { useCart, useGeoDiscount } from 'frontastic';
import {
  GeoPricingResult,
  GeoPricingTestData,
} from '../monarch/rules/geoPricing/types';

type Option = {
  title: string;
  value: string;
};

type GeoCountrySelectProps = {
  options: Option[];
  disabled: boolean;
  products?: ProductDetails;
};

export const CountrySelect = ({
  options,
  disabled,
  products,
}: GeoCountrySelectProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { data: cartData } = useCart();
  const {
    isAutoApplied,
    validateAndUpdateDiscount,
    setBillingCountry,
    cookieCountry,
  } = useGeoDiscount();

  const detectAndTrackCountryMismatch = (selectedCountry: string) => {
    if (!cookieCountry || !selectedCountry) {
      return;
    }

    if (cookieCountry !== selectedCountry) {
      const { elementViewed } = elementEvents({
        webElement: {
          elementType: ElementType.Component,
          name: 'country-mismatch',
          text: `Geo: ${cookieCountry}, Selected: ${selectedCountry}`,
        },
      });

      elementViewed({
        customDimensions: [
          {
            key: 'geolocated_country',
            value: cookieCountry,
          },
          {
            key: 'selected_country',
            value: selectedCountry,
          },
          {
            key: 'event_type',
            value: 'country_mismatch',
          },
        ],
      });
    }
  };

  const handleChange = async (
    value: string,
    geoPricingData?: GeoPricingTestData,
  ) => {
    try {
      // Always update billing country in store so geo discount eligibility
      // (billing vs IP) is correct when user re-selects their country
      setBillingCountry(value);

      if (!isAutoApplied) {
        return;
      }

      // Track country mismatch event (only when geo discount flow is active)
      detectAndTrackCountryMismatch(value);

      const geoDiscount = getDiscountByCountry({
        country: value,
        products,
        data: geoPricingData,
      });

      const discount: GeoDiscount = geoDiscount
        ? {
            ...geoDiscount,
            productKind:
              hasBundleId(cartData?.lineItems ?? []) ||
              hasProfessionalCertificate(cartData?.lineItems ?? [])
                ? 'program'
                : 'course',
          }
        : {
            discountCode: '',
            discountPercentage: 0,
          };

      setIsLoading(true);

      await validateAndUpdateDiscount(discount);

      setIsLoading(false);
    } catch {
      setIsLoading(false);
    }
  };

  return (
    <MonarchDataConsumer<GeoPricingResult> experimentKey="useGeoPricing">
      {(geoPricingData) => (
        <Form.Select
          name="country"
          label={
            <FormattedMessage
              id="payment.form.address.country"
              defaultMessage="Country (required)"
              description="Label for Country Select"
            />
          }
          options={options}
          autocomplete="country"
          styleClasses="col-lg-6"
          disabled={isLoading || disabled}
          onChange={(value) => {
            // had to disable this rule since the promise is awaited inside the 'handleChange' function
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            handleChange(value, geoPricingData?.data);
          }}
        />
      )}
    </MonarchDataConsumer>
  );
};
